export interface CompanyItem {
  page_id: string;
  company_id: number;
  branch_id: number;
}

export interface AvailableOptions {
  companyIds: number[];
  branchIds: number[];
  pageIds: string[];
}

export class CompanyBiMap {
  private byCompany = new Map<number, CompanyItem[]>();
  private byBranch = new Map<number, CompanyItem[]>();
  private byPage = new Map<string, CompanyItem[]>();
  private readonly allData: CompanyItem[];

  private cachedAll: AvailableOptions | null = null;
  private cache = new Map<string, AvailableOptions>();

  constructor(data: CompanyItem[]) {
    this.allData = data;

    data.forEach((item) => {
      if (!this.byCompany.has(item.company_id)) {
        this.byCompany.set(item.company_id, []);
      }
      (this.byCompany.get(item.company_id) as unknown as CompanyItem[]).push(item);

      if (!this.byBranch.has(item.branch_id)) {
        this.byBranch.set(item.branch_id, []);
      }
      (this.byBranch.get(item.branch_id) as unknown as CompanyItem[]).push(item);

      if (!this.byPage.has(item.page_id)) {
        this.byPage.set(item.page_id, []);
      }
      (this.byPage.get(item.page_id) as unknown as CompanyItem[]).push(item);
    });
  }

  private extractUnique(items: CompanyItem[]): AvailableOptions {
    const companyIds = [...new Set(items.map((i) => i.company_id))];
    const branchIds = [...new Set(items.map((i) => i.branch_id))];
    const pageIds = [...new Set(items.map((i) => i.page_id))];
    return { companyIds, branchIds, pageIds };
  }

  private getCacheKey(prefix: string, ids: number[] | string[]): string {
    return `${prefix}:${ids.sort().join(',')}`;
  }

  getAll(): AvailableOptions {
    if (!this.cachedAll) {
      this.cachedAll = this.extractUnique(this.allData);
    }
    return this.cachedAll;
  }

  getForCompanies(companyIds: number[]): AvailableOptions {
    if (companyIds.length === 0) return this.getAll();

    const cacheKey = this.getCacheKey('companies', companyIds);
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const items = companyIds.flatMap((id) => this.byCompany.get(id) ?? []);
    const result = this.extractUnique(items);
    this.cache.set(cacheKey, result);
    return result;
  }

  getForBranches(branchIds: number[]): AvailableOptions {
    if (branchIds.length === 0) return this.getAll();

    const cacheKey = this.getCacheKey('branches', branchIds);
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const items = branchIds.flatMap((id) => this.byBranch.get(id) ?? []);
    const result = this.extractUnique(items);
    this.cache.set(cacheKey, result);
    return result;
  }

  getForSelection(companyIds: number[] = [], branchIds: number[] = []): AvailableOptions {
    const hasCompanies = companyIds.length > 0;
    const hasBranches = branchIds.length > 0;

    if (!hasCompanies && !hasBranches) return this.getAll();

    const cacheKey = this.getCacheKey('selection', [...companyIds.map(String), ...branchIds.map(String)]);
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    let items: CompanyItem[];

    if (hasCompanies && hasBranches) {
      const branchSet = new Set(branchIds);
      items = companyIds.flatMap((id) => this.byCompany.get(id) ?? []).filter((item) => branchSet.has(item.branch_id));
    } else if (hasCompanies) {
      items = companyIds.flatMap((id) => this.byCompany.get(id) ?? []);
    } else {
      items = branchIds.flatMap((id) => this.byBranch.get(id) ?? []);
    }

    const result = this.extractUnique(items);
    this.cache.set(cacheKey, result);
    return result;
  }
}
